import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Email Logs Component
 * Displays email notification history for admin review
 */
const EmailLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchEmailLogs();
  }, []);

  const fetchEmailLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/email-logs`);
      setLogs(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => {
    if (filterStatus === 'all') return true;
    return log.status === filterStatus;
  });

  const EmailLogCard = ({ log }) => (
    <div className="border rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="font-semibold text-gray-900">{log.subject}</h3>
          <p className="text-sm text-gray-600">To: {log.to}</p>
        </div>
        <div className="flex flex-col items-end space-y-1">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            log.status === 'sent' ? 'bg-green-100 text-green-800' :
            log.status === 'failed' ? 'bg-red-100 text-red-800' :
            'bg-yellow-100 text-yellow-800'
          }`}>
            {log.status.toUpperCase()}
          </span>
          <span className="text-xs text-gray-500">
            {new Date(log.timestamp).toLocaleDateString()}
          </span>
        </div>
      </div>
      
      <div className="space-y-2 text-sm">
        <div>
          <p className="font-medium text-gray-800">Request Type:</p>
          <p className="text-gray-600 capitalize">{log.request_type}</p>
        </div>
        
        <div>
          <p className="font-medium text-gray-800">Email Content:</p>
          <div className="bg-gray-50 p-3 rounded text-xs text-gray-700 max-h-24 overflow-y-auto">
            {log.content}
          </div>
        </div>
        
        {log.error && (
          <div>
            <p className="font-medium text-red-800">Error:</p>
            <div className="bg-red-50 p-3 rounded text-xs text-red-700">
              {log.error}
            </div>
          </div>
        )}
        
        {log.request_data && (
          <details className="mt-3">
            <summary className="cursor-pointer font-medium text-gray-800 text-xs">
              View Request Data
            </summary>
            <div className="bg-gray-50 p-3 rounded text-xs text-gray-700 mt-2">
              <pre className="whitespace-pre-wrap">
                {JSON.stringify(log.request_data, null, 2)}
              </pre>
            </div>
          </details>
        )}
        
        <div className="text-xs text-gray-500 pt-2 border-t">
          <p>Timestamp: {new Date(log.timestamp).toLocaleString()}</p>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-4xl mb-4">📧</div>
          <p className="text-gray-600">Loading email logs...</p>
        </div>
      </div>
    );
  }

  const sentEmails = logs.filter(l => l.status === 'sent').length;
  const failedEmails = logs.filter(l => l.status === 'failed').length;
  const loggedEmails = logs.filter(l => l.status === 'logged_demo').length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-gray-900">Email Notification Logs</h2>
        <p className="text-gray-600 mt-1">Review email notification history and delivery status</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
          <div className="flex items-center">
            <div className="text-blue-600 text-3xl mr-4">📧</div>
            <div>
              <p className="text-sm text-blue-600 font-medium">Total Emails</p>
              <p className="text-2xl font-bold text-blue-800">{logs.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 rounded-lg p-6 border border-green-200">
          <div className="flex items-center">
            <div className="text-green-600 text-3xl mr-4">✅</div>
            <div>
              <p className="text-sm text-green-600 font-medium">Successfully Sent</p>
              <p className="text-2xl font-bold text-green-800">{sentEmails}</p>
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 rounded-lg p-6 border border-yellow-200">
          <div className="flex items-center">
            <div className="text-yellow-600 text-3xl mr-4">📝</div>
            <div>
              <p className="text-sm text-yellow-600 font-medium">Demo Mode (Logged)</p>
              <p className="text-2xl font-bold text-yellow-800">{loggedEmails}</p>
            </div>
          </div>
        </div>

        <div className="bg-red-50 rounded-lg p-6 border border-red-200">
          <div className="flex items-center">
            <div className="text-red-600 text-3xl mr-4">❌</div>
            <div>
              <p className="text-sm text-red-600 font-medium">Failed</p>
              <p className="text-2xl font-bold text-red-800">{failedEmails}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Email History</h3>
            <p className="text-sm text-gray-600">Filter emails by delivery status</p>
          </div>
          
          <div className="flex space-x-4">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Emails</option>
              <option value="sent">Successfully Sent</option>
              <option value="logged_demo">Demo Mode (Logged)</option>
              <option value="failed">Failed</option>
            </select>
            
            <button
              onClick={fetchEmailLogs}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              🔄 Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Email Logs List */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">
          Email Logs ({filteredLogs.length})
        </h3>
        
        {filteredLogs.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-6xl mb-4">📭</div>
            <p className="text-gray-500">
              {filterStatus !== 'all' 
                ? `No emails with status "${filterStatus}" found` 
                : 'No email logs found'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredLogs.map((log, index) => (
              <EmailLogCard key={log._id || index} log={log} />
            ))}
          </div>
        )}
      </div>

      {/* SMTP Configuration Info */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">📨 SMTP Configuration</h3>
        <div className="text-sm text-gray-600 space-y-2">
          <p>• <strong>Demo Mode:</strong> Emails are logged to database when SMTP is not configured</p>
          <p>• <strong>Production:</strong> Configure SMTP settings in backend .env file for actual email delivery</p>
          <p>• <strong>Failed Emails:</strong> Usually indicate SMTP connection issues or invalid credentials</p>
          <p>• <strong>Email Logs:</strong> Stored permanently for audit and troubleshooting purposes</p>
        </div>
        
        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
          <p className="text-sm text-yellow-800">
            <strong>Current Status:</strong> {sentEmails > 0 ? 'SMTP appears to be configured and working' : 'Running in demo mode (emails logged only)'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailLogs;